import {
  FC,
  createContext,
  PropsWithChildren,
  useContext,
  useMemo
} from 'react'
import usePopup, { PopupReturnTypeWithPayload } from 'hooks/usePopup'
import {
  deleteBankAccountPopupWithPayload,
  setAsDefaultReceivableAccountPopupWithPayload,
  verifyDepositsPopupWithPayload,
  cantProceedWithDefaultReceivableAccountWithPayload,
  thisAccountIsDefaultReceivableAccountWithPayload,
  setAnotherDefaultReceivableAccountPopupWithPayload,
  DEFAULT_RECEIVABLE_ACCOUNT_POPUP_TITLES
} from '../constants'
import {
  DeleteBankAccountPopupPayload,
  SetAsDefaultReceivableAccountPopupPayload,
  VerifyDepositsPopupPayload,
  CantProceedWithDefaultReceivableAccountPopupPayload,
  ThisAccountIsDefaultReceivableAccountPopupPayload,
  SetAnotherDefaultReceivableAccountPopupPayload
} from '../types'

type ContextProps = {
  deleteBankAccountPopup: PopupReturnTypeWithPayload<DeleteBankAccountPopupPayload>
  verifyDepositsPopup: PopupReturnTypeWithPayload<VerifyDepositsPopupPayload>
  setAsDefaultReceivableAccountPopup: PopupReturnTypeWithPayload<SetAsDefaultReceivableAccountPopupPayload>
  cantProceedWithDefaultReceivableAccountPopup: PopupReturnTypeWithPayload<CantProceedWithDefaultReceivableAccountPopupPayload>
  thisAccountIsDefaultReceivableAccountPopup: PopupReturnTypeWithPayload<ThisAccountIsDefaultReceivableAccountPopupPayload>
  setAnotherDefaultReceivableAccountPopup: PopupReturnTypeWithPayload<SetAnotherDefaultReceivableAccountPopupPayload>
}

const basePopup = (title: string) => ({
  title
})

const BankAccountPopupContext = createContext<ContextProps>({
  deleteBankAccountPopup: deleteBankAccountPopupWithPayload,
  verifyDepositsPopup: verifyDepositsPopupWithPayload,
  setAsDefaultReceivableAccountPopup:
    setAsDefaultReceivableAccountPopupWithPayload,
  cantProceedWithDefaultReceivableAccountPopup:
    cantProceedWithDefaultReceivableAccountWithPayload,
  thisAccountIsDefaultReceivableAccountPopup:
    thisAccountIsDefaultReceivableAccountWithPayload,
  setAnotherDefaultReceivableAccountPopup:
    setAnotherDefaultReceivableAccountPopupWithPayload
})

const BankAccountPopupProvider: FC<PropsWithChildren> = ({ children }) => {
  const deleteBankAccountPopupProps = usePopup<DeleteBankAccountPopupPayload>(
    basePopup('Delete bank account?')
  )
  const verifyDepositsPopupProps = usePopup<VerifyDepositsPopupPayload>(
    basePopup('Verify bank account')
  )

  const setAsDefaultReceivableAccountPopupProps =
    usePopup<SetAsDefaultReceivableAccountPopupPayload>(
      basePopup('Set as default receivable account?')
    )

  const cantProceedWithDefaultReceivableAccountPopupProps =
    usePopup<CantProceedWithDefaultReceivableAccountPopupPayload>(
      basePopup(DEFAULT_RECEIVABLE_ACCOUNT_POPUP_TITLES.cantProceed.delete)
    )

  const thisAccountIsDefaultReceivableAccountPopupProps =
    usePopup<ThisAccountIsDefaultReceivableAccountPopupPayload>(
      basePopup(
        DEFAULT_RECEIVABLE_ACCOUNT_POPUP_TITLES.thisAccountIsDefault.delete
      )
    )
  const setAnotherDefaultReceivableAccountPopupProps =
    usePopup<SetAnotherDefaultReceivableAccountPopupPayload>(
      basePopup(
        DEFAULT_RECEIVABLE_ACCOUNT_POPUP_TITLES.setAnotherDefault.delete
      )
    )
  const context = useMemo(
    () => ({
      deleteBankAccountPopup: deleteBankAccountPopupProps,
      verifyDepositsPopup: verifyDepositsPopupProps,
      setAsDefaultReceivableAccountPopup:
        setAsDefaultReceivableAccountPopupProps,
      cantProceedWithDefaultReceivableAccountPopup:
        cantProceedWithDefaultReceivableAccountPopupProps,
      thisAccountIsDefaultReceivableAccountPopup:
        thisAccountIsDefaultReceivableAccountPopupProps,
      setAnotherDefaultReceivableAccountPopup:
        setAnotherDefaultReceivableAccountPopupProps
    }),
    [
      deleteBankAccountPopupProps,
      verifyDepositsPopupProps,
      setAsDefaultReceivableAccountPopupProps,
      cantProceedWithDefaultReceivableAccountPopupProps,
      setAnotherDefaultReceivableAccountPopupProps,
      thisAccountIsDefaultReceivableAccountPopupProps
    ]
  )

  return (
    <BankAccountPopupContext.Provider value={context}>
      {children}
    </BankAccountPopupContext.Provider>
  )
}

export const useBankAccountPopup = () => useContext(BankAccountPopupContext)

export default BankAccountPopupProvider
